/**
 * Funções puras de combos e promoções (vigência, preço promocional, preço/desmembramento de combo e rateio fiscal).
 * Manter idêntico a like-desktop/shared/helpers/combo-promocao.helper.ts (muda só o import dos tipos).
 * Referência: like-docs/compartilhado/combos-promocoes-fiscal.md
 */
import type {
  Categoria,
  Desconto,
  EscolhaCombo,
  Fiscal,
  GrupoCombo,
  ItemDesmembrado,
  ItemRateado,
  OpcaoGrupoCombo,
  Produto,
  TabelaPreco,
  Vigencia,
} from '@mobile/models/cardapio/cardapio.model';

const FUSO = 'America/Sao_Paulo';
const DIAS_SEMANA: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export const arredondar = (valor: number): number => Math.round((valor + Number.EPSILON) * 100) / 100;

interface InstanteSP {
  /** 'YYYY-MM-DD' */
  dia: string;
  /** 0 = domingo */
  diaSemana: number;
  /** Minutos desde 00:00 */
  minutos: number;
}

function instanteSaoPaulo(agora: Date): InstanteSP {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: FUSO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(agora);
  const valor = (tipo: string) => partes.find(p => p.type === tipo)?.value ?? '';

  return {
    dia: `${valor('year')}-${valor('month')}-${valor('day')}`,
    diaSemana: DIAS_SEMANA[valor('weekday')] ?? 0,
    minutos: (Number(valor('hour')) % 24) * 60 + Number(valor('minute')),
  };
}

function emMinutos(hora?: string): number | undefined {
  if (!hora) {
    return undefined;
  }
  const [h, m] = hora.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : undefined;
}

/** Vigência ausente = sempre vigente. Avalia no fuso de São Paulo. */
export function estaVigente(vigencia: Vigencia | undefined, agora: Date = new Date()): boolean {
  if (!vigencia) {
    return true;
  }

  const { dia, diaSemana, minutos } = instanteSaoPaulo(agora);
  if ((vigencia.inicio && dia < vigencia.inicio) || (vigencia.fim && dia > vigencia.fim)) {
    return false;
  }

  const dias = vigencia.diasSemana ?? [];
  const diaValido = (d: number) => !dias.length || dias.includes(d);
  const inicio = emMinutos(vigencia.horaInicio);
  const fim = emMinutos(vigencia.horaFim);

  if (inicio === undefined && fim === undefined) {
    return diaValido(diaSemana);
  }
  if (inicio !== undefined && fim !== undefined && inicio > fim) {
    // Atravessa a meia-noite: a madrugada pertence ao dia em que a janela começou.
    return (minutos >= inicio && diaValido(diaSemana)) || (minutos < fim && diaValido((diaSemana + 6) % 7));
  }
  if (!diaValido(diaSemana)) {
    return false;
  }
  return (inicio === undefined || minutos >= inicio) && (fim === undefined || minutos < fim);
}

/** Preço do produto no tamanho: `precos[t] ?? tabela.precos[t]`. */
export function precoDoProduto(produto: Produto, tabelas: TabelaPreco[], tamanhoId: string = 'unico'): number | undefined {
  const tabela = produto.tabelaPrecoId ? tabelas.find(t => t.id === produto.tabelaPrecoId) : undefined;
  return produto.precos?.[tamanhoId] ?? tabela?.precos[tamanhoId];
}

export function aplicarDesconto(preco: number, desconto: Desconto): number {
  switch (desconto.tipo) {
    case 'percentual':
      return arredondar(Math.max(0, preco * (1 - desconto.valor / 100)));
    case 'valor':
      return arredondar(Math.max(0, preco - desconto.valor));
    case 'preco':
      return arredondar(Math.max(0, desconto.valor));
  }
}

/** Ids informados mais todos os ancestrais deles. */
function comAncestrais(categorias: Categoria[], ids: string[]): Set<string> {
  const resultado = new Set<string>();
  for (const id of ids) {
    let atual = categorias.find(c => c.id === id);
    while (atual && !resultado.has(atual.id)) {
      resultado.add(atual.id);
      const parentId = atual.parentId;
      atual = parentId ? categorias.find(c => c.id === parentId) : undefined;
    }
    resultado.add(id);
  }
  return resultado;
}

export function promocaoAplicaA(promocao: Produto, produto: Produto, tamanhoId: string, categorias: Categoria[]): boolean {
  const alvo = promocao.alvo;
  if (!alvo) {
    return false;
  }
  if (alvo.tamanhoIds?.length && !alvo.tamanhoIds.includes(tamanhoId)) {
    return false;
  }
  if (alvo.produtoIds?.includes(produto.id)) {
    return true;
  }
  if (alvo.categoriaIds?.length) {
    const doProduto = comAncestrais(categorias, produto.categoriaIds);
    return alvo.categoriaIds.some(id => doProduto.has(id));
  }
  return false;
}

export interface PromocaoAplicada {
  promocao: Produto;
  preco: number;
}

/** Melhor (menor preço) promoção ativa e vigente para o produto/tamanho; undefined se nenhuma baixa o preço. */
export function melhorPromocao(
  promocoes: Produto[],
  produto: Produto,
  precoBase: number,
  tamanhoId: string,
  categorias: Categoria[],
  agora: Date = new Date()
): PromocaoAplicada | undefined {
  let melhor: PromocaoAplicada | undefined;

  for (const promocao of promocoes) {
    if (promocao.tipo !== 'promocao' || !promocao.ativo || !promocao.desconto) {
      continue;
    }
    if (!estaVigente(promocao.vigencia, agora) || !promocaoAplicaA(promocao, produto, tamanhoId, categorias)) {
      continue;
    }
    const preco = aplicarDesconto(precoBase, promocao.desconto);
    if (preco < precoBase && (!melhor || preco < melhor.preco)) {
      melhor = { promocao, preco };
    }
  }
  return melhor;
}

/** Fiscal do produto ou, na falta, o `fiscalPadrao` da categoria mais próxima. */
export function fiscalDoProduto(produto: Produto, categorias: Categoria[]): Fiscal | undefined {
  if (produto.fiscal) {
    return produto.fiscal;
  }
  for (const id of produto.categoriaIds) {
    const visitados = new Set<string>();
    let atual = categorias.find(c => c.id === id);
    while (atual && !visitados.has(atual.id)) {
      if (atual.fiscalPadrao) {
        return atual.fiscalPadrao;
      }
      visitados.add(atual.id);
      const parentId = atual.parentId;
      atual = parentId ? categorias.find(c => c.id === parentId) : undefined;
    }
  }
  return undefined;
}

/** Preço do combo: precoFixo + adicionais das opções escolhidas. */
export function precoDoCombo(combo: Produto, escolhas: EscolhaCombo[]): number {
  const adicionais = escolhas.reduce(
    (soma, escolha) => soma + escolha.opcoes.reduce((s, opcao) => s + (opcao.valorAdicional ?? 0), 0),
    0
  );
  return arredondar((combo.precoFixo ?? 0) + adicionais);
}

/** Quantas opções a escolha do grupo deve ter. */
export function totalEscolhasDoGrupo(grupo: GrupoCombo): number {
  return grupo.multiParte ? grupo.quantidade * grupo.multiParte.partes : grupo.quantidade;
}

export interface ModeloCombo {
  produtos: Produto[];
  tabelas: TabelaPreco[];
  categorias: Categoria[];
}

const ORDEM_CODIGO = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

/** Mesma ordem do pedido: categoria na árvore (pai antes dos filhos) e, dentro dela, código interno (ou número no início do nome). */
export function ordenarProdutosDoCardapio<T extends Produto>(produtos: T[], categorias: Categoria[]): T[] {
  const ids = new Set(categorias.map(c => c.id));
  const porOrdem = (a: Categoria, b: Categoria) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR');
  const posicao = new Map<string, number>();
  const visitar = (parentId?: string): void => {
    categorias
      .filter(c => (parentId ? c.parentId === parentId : !c.parentId || !ids.has(c.parentId)))
      .sort(porOrdem)
      .forEach(c => {
        posicao.set(c.id, posicao.size);
        visitar(c.id);
      });
  };
  visitar();

  const primeira = (p: T) => Math.min(...p.categoriaIds.map(id => posicao.get(id) ?? Number.MAX_SAFE_INTEGER), Number.MAX_SAFE_INTEGER);
  const codigo = (p: T) => p.codigo_interno || p.nome.split('-')[0].trim();

  return [...produtos].sort((a, b) =>
    primeira(a) - primeira(b) || ORDEM_CODIGO.compare(codigo(a), codigo(b)) || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Sabores de um grupo multi-partes: produtos ativos da categoria (e filhas) com preço no tamanho. */
export function saboresDoGrupo(grupo: GrupoCombo, modelo: ModeloCombo): OpcaoGrupoCombo[] {
  const { categoriaId, tamanhoId } = grupo.multiParte ?? {};
  if (!categoriaId || !tamanhoId) {
    return [];
  }
  const sabores = modelo.produtos.filter(p => p.tipo === 'produto' && p.ativo !== false
    && p.categoriaIds.some(id => comAncestrais(modelo.categorias, [id]).has(categoriaId))
    && precoDoProduto(p, modelo.tabelas, tamanhoId) !== undefined);
  return ordenarProdutosDoCardapio(sabores, modelo.categorias).map(p => ({ produtoId: p.id, tamanhoId }));
}

/** Lista de problemas nas escolhas (vazia = válidas). Com `modelo`, confere também os sabores dos grupos multi-partes. */
export function validarEscolhasCombo(combo: Produto, escolhas: EscolhaCombo[], modelo?: ModeloCombo): string[] {
  const erros: string[] = [];

  for (const grupo of combo.grupos ?? []) {
    const escolha = escolhas.find(e => e.grupoId === grupo.id);
    const feitas = escolha?.opcoes ?? [];
    const total = totalEscolhasDoGrupo(grupo);
    const partes = grupo.multiParte?.partes ?? 1;

    if (feitas.length > total) {
      erros.push(`"${grupo.titulo}": escolha no máximo ${total}.`);
    } else if (grupo.obrigatorio && feitas.length < total) {
      erros.push(`"${grupo.titulo}": escolha ${total}.`);
    } else if (feitas.length % partes !== 0) {
      erros.push(`"${grupo.titulo}": complete os ${partes} sabores de cada pizza.`);
    }

    const validas = grupo.multiParte
      ? (modelo ? saboresDoGrupo(grupo, modelo) : undefined)
      : grupo.opcoes;
    const invalida = (f: OpcaoGrupoCombo) => grupo.multiParte
      ? f.tamanhoId !== grupo.multiParte.tamanhoId || (!!validas && !opcaoExiste(validas, f.produtoId, f.tamanhoId))
      : !opcaoExiste(grupo.opcoes, f.produtoId, f.tamanhoId);
    if (feitas.some(invalida)) {
      erros.push(`"${grupo.titulo}": opção inválida.`);
    }
  }
  return erros;
}

function opcaoExiste(opcoes: OpcaoGrupoCombo[], produtoId: string, tamanhoId?: string): boolean {
  return opcoes.some(o => o.produtoId === produtoId && o.tamanhoId === tamanhoId);
}

/**
 * Produtos reais do combo (itens fixos + escolhas), com preço de tabela. Itens iguais são somados.
 * Sabor de pizza multi-partes vira "Sabor (1/N)" com 1/N do preço de tabela do tamanho.
 * Produto/preço não encontrado é ignorado: combo com referência quebrada deve ser tratado antes (ver `validarEscolhasCombo`).
 */
export function desmembrarCombo(
  combo: Produto,
  escolhas: EscolhaCombo[],
  produtos: Produto[],
  tabelas: TabelaPreco[],
  categorias: Categoria[]
): ItemDesmembrado[] {
  interface Referencia {
    produtoId: string;
    tamanhoId?: string;
    quantidade: number;
    /** Pizza dividida em `partes` sabores. */
    partes?: number;
  }

  const referencias: Referencia[] = [
    ...(combo.itensFixos ?? []).map(i => ({ produtoId: i.produtoId, tamanhoId: i.tamanhoId, quantidade: i.quantidade })),
    ...escolhas.reduce<Referencia[]>((acc, e) => {
      const partes = combo.grupos?.find(g => g.id === e.grupoId)?.multiParte?.partes ?? 1;
      return acc.concat(e.opcoes.map(o => ({
        produtoId: o.produtoId,
        tamanhoId: o.tamanhoId,
        quantidade: 1,
        ...(partes > 1 ? { partes } : {}),
      })));
    }, []),
  ];

  const itens = new Map<string, ItemDesmembrado>();
  for (const ref of referencias) {
    const produto = produtos.find(p => p.id === ref.produtoId);
    const precoTamanho = produto ? precoDoProduto(produto, tabelas, ref.tamanhoId) : undefined;
    if (!produto || precoTamanho === undefined) {
      continue;
    }
    const chave = `${ref.produtoId}|${ref.tamanhoId ?? ''}|${ref.partes ?? ''}`;
    const existente = itens.get(chave);
    if (existente) {
      existente.quantidade += ref.quantidade;
    } else {
      const fiscal = fiscalDoProduto(produto, categorias);
      itens.set(chave, {
        produtoId: produto.id,
        nome: ref.partes ? `${produto.nome} (1/${ref.partes})` : produto.nome,
        ...(ref.tamanhoId ? { tamanhoId: ref.tamanhoId } : {}),
        quantidade: ref.quantidade,
        precoTabela: ref.partes ? arredondar(precoTamanho / ref.partes) : precoTamanho,
        ...(fiscal ? { fiscal } : {}),
      });
    }
  }
  return [...itens.values()];
}

/**
 * Rateia o desconto do combo entre os itens, proporcional ao valor de tabela (vDesc da NFC-e).
 * O último item recebe o saldo, então Σ valorLiquido = precoFinalCombo (sem divergência de centavo).
 */
export function ratearDescontoCombo(itens: ItemDesmembrado[], precoFinalCombo: number): ItemRateado[] {
  const totalItem = (i: ItemDesmembrado) => arredondar(i.precoTabela * i.quantidade);
  const somaTabela = arredondar(itens.reduce((soma, i) => soma + totalItem(i), 0));
  const descontoTotal = arredondar(somaTabela - precoFinalCombo);

  if (descontoTotal <= 0 || somaTabela <= 0) {
    return itens.map(i => ({ ...i, valorDesconto: 0, valorLiquido: totalItem(i) }));
  }

  let acumulado = 0;
  return itens.map((item, indice) => {
    const total = totalItem(item);
    const valorDesconto =
      indice === itens.length - 1
        ? arredondar(descontoTotal - acumulado)
        : arredondar(descontoTotal * (total / somaTabela));
    acumulado = arredondar(acumulado + valorDesconto);

    return { ...item, valorDesconto, valorLiquido: arredondar(total - valorDesconto) };
  });
}
